/* Ancestral Brotherhood — The Apothecary motion layer.
 *
 * Loaded (deferred) from sections/ab-supplements.liquid, after the shared
 * GSAP + ScrollTrigger bundles that snippets/ab-motion.liquid already puts on
 * every page.
 *
 * Contract, same as ab-anim.js:
 *  1. Nothing is hidden unless html.ab-sup-anim is present, and this file only
 *     adds that class once GSAP and ScrollTrigger are both confirmed. A failed
 *     CDN load leaves the page fully readable.
 *  2. prefers-reduced-motion returns before anything is staged.
 *  3. No transform, filter or will-change goes on an ancestor of a pinned
 *     section. Nothing here pins, but the page-background section sits above
 *     these elements, so the rule still applies.
 *
 * The torch is deliberately separate from the GSAP block below: it is a
 * pointer affordance, not scroll motion, so it still runs if GSAP never
 * arrives and it is never bound on a touch device.
 */
(function () {
  var still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var fine = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  /* ------------------------------------------------ the torch ---------- */
  /* Writes the beam position as percentages on the hovered card. Everything
     visual — the warm pool, the falloff, the flicker — is CSS. A phone has no
     cursor to follow and :hover sticks after a tap, so this is fine-pointer
     only. */
  function torch() {
    if (!fine) return;
    var grids = document.querySelectorAll('.ab-sup__grid');
    if (!grids.length) return;

    grids.forEach(function (grid) {
      var frame = 0;
      var pending = null;

      grid.addEventListener(
        'pointermove',
        function (e) {
          var card = e.target.closest ? e.target.closest('.ab-sup__card') : null;
          if (!card) return;
          var beam = card.querySelector('.ab-sup__torch');
          if (!beam) return;

          pending = { beam: beam, card: card, x: e.clientX, y: e.clientY };
          if (frame) return;
          frame = requestAnimationFrame(function () {
            frame = 0;
            if (!pending) return;
            var r = pending.card.getBoundingClientRect();
            var tx = ((pending.x - r.left) / r.width) * 100;
            var ty = ((pending.y - r.top) / r.height) * 100;
            pending.beam.style.setProperty('--tx', tx.toFixed(1) + '%');
            pending.beam.style.setProperty('--ty', ty.toFixed(1) + '%');
          });
        },
        { passive: true }
      );

      // Leaving the card parks the beam back in the middle so the next
      // hover does not fade in from wherever the last one ended.
      grid.addEventListener(
        'pointerout',
        function (e) {
          var card = e.target.closest ? e.target.closest('.ab-sup__card') : null;
          if (!card || (e.relatedTarget && card.contains(e.relatedTarget))) return;
          var beam = card.querySelector('.ab-sup__torch');
          if (!beam) return;
          beam.style.setProperty('--tx', '50%');
          beam.style.setProperty('--ty', '42%');
        },
        { passive: true }
      );
    });
  }

  /* ------------------------------------------------ scroll motion ------- */
  var tries = 0;
  function boot() {
    if (!window.gsap || !window.ScrollTrigger) {
      if (tries++ < 40) return setTimeout(boot, 100);
      return;
    }

    var roots = document.querySelectorAll('.ab-sup');
    if (!roots.length) return;

    gsap.registerPlugin(ScrollTrigger);
    document.documentElement.classList.add('ab-sup-anim');

    var EASE = 'power3.out';

    roots.forEach(function (root) {
      var crest = root.querySelector('.ab-sup__crest');
      var copy = root.querySelectorAll('.ab-sup__eyebrow, .ab-sup__h, .ab-sup__lede, .ab-sup__creed');

      if (copy.length) {
        gsap.to(copy, {
          opacity: 1,
          y: 0,
          duration: 0.9,
          stagger: 0.09,
          ease: EASE,
          scrollTrigger: { trigger: root, start: 'top 85%' }
        });
      }

      // The crest settles in, then drifts a little as the page moves. Scrub
      // rather than a loop so it reads as weight, not as a GIF.
      if (crest) {
        gsap.fromTo(
          crest,
          { opacity: 0, scale: 0.94, y: 16 },
          {
            opacity: 1,
            scale: 1,
            y: 0,
            duration: 1.1,
            ease: 'power3.out',
            scrollTrigger: { trigger: root, start: 'top 90%' }
          }
        );
        gsap.to(crest, {
          yPercent: -9,
          ease: 'none',
          scrollTrigger: { trigger: root, start: 'top top', end: '+=90%', scrub: 0.6 }
        });
      }

      root.querySelectorAll('.ab-sup__cat').forEach(function (cat) {
        var head = cat.querySelectorAll('.ab-sup__catnum, .ab-sup__cattitle, .ab-sup__catline');
        var rule = cat.querySelector('.ab-sup__rule');
        var cards = cat.querySelectorAll('.ab-sup__card');

        var tl = gsap.timeline({ scrollTrigger: { trigger: cat, start: 'top 82%' } });

        if (head.length) {
          tl.to(head, { opacity: 1, y: 0, duration: 0.7, stagger: 0.07, ease: EASE }, 0);
        }
        if (rule) {
          tl.to(rule, { scaleX: 1, duration: 0.9, ease: 'power2.inOut' }, 0.12);
        }
        if (cards.length) {
          tl.to(
            cards,
            { opacity: 1, y: 0, scale: 1, duration: 0.75, stagger: 0.07, ease: 'back.out(1.5)' },
            0.2
          );
        }

        // Each bottle drifts inside its own tile as the card crosses the
        // screen. Small numbers on purpose — depth, not sliding.
        cards.forEach(function (card) {
          var img = card.querySelector('.ab-sup__media img');
          if (!img) return;
          gsap.fromTo(
            img,
            { yPercent: 4 },
            {
              yPercent: -4,
              ease: 'none',
              scrollTrigger: { trigger: card, start: 'top bottom', end: 'bottom top', scrub: 0.5 }
            }
          );
        });
      });

      var foot = root.querySelectorAll('.ab-sup__foot, .ab-sup__legal');
      if (foot.length) {
        gsap.to(foot, {
          opacity: 1,
          y: 0,
          duration: 0.8,
          stagger: 0.1,
          ease: EASE,
          scrollTrigger: { trigger: foot[0], start: 'top 92%' }
        });
      }
    });

    // Lazy images change layout height as they arrive; without this the
    // triggers measured against the pre-load height and fired early.
    window.addEventListener('load', function () { ScrollTrigger.refresh(); });
  }

  function start() {
    torch();
    if (!still) boot();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
